import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  TeamCreateInput,
  TeamDetails,
  TeamListFilters,
  TeamListItem,
  TeamMember,
  TeamSearchResult,
  TeamUpdateInput,
} from '../types/team';
import { CONFIGURATION_RECORD_INACTIVE } from '../constants/configuration';
import { nextDisplayOrder } from '../utils/displayOrder';
import { getMasterDataStatusLabel } from '../utils/masterDataStatus';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

const DETAILS_PAGE_SIZE = 100;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function requiredId(value: unknown, label: string): number {
  const parsed = numberOrNull(value);
  if (parsed === null || parsed <= 0) {
    throw Object.assign(new Error(`${label} is missing`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return parsed;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function requiredText(value: unknown, label: string): string {
  const text = textOrNull(value);
  if (!text) {
    throw Object.assign(new Error(`${label} is required`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return text;
}

function boolOrNull(value: unknown): boolean | null {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return null;
}

function emptyToNull(value: string | null | undefined): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function teamLeadNameFrom(row: ObjectValue): string | null {
  return (
    textOrNull(row.teamLeadName) ??
    textOrNull(row.teamLeaderName) ??
    textOrNull(row.teamLeaderDisplayName)
  );
}

function normalizeListItem(row: ObjectValue): TeamListItem | null {
  try {
    const isActive = boolOrNull(row.isActive);
    return {
      teamId: requiredId(row.teamId, 'teamId'),
      teamCode: textOrNull(row.teamCode) ?? String(row.teamId),
      teamName: textOrNull(row.teamName) ?? 'Team',
      description: textOrNull(row.description),
      departmentId: numberOrNull(row.departmentId),
      departmentName: textOrNull(row.departmentName),
      locationId: numberOrNull(row.locationId),
      locationName: textOrNull(row.locationName),
      teamLeaderId: numberOrNull(row.teamLeaderId),
      teamLeadName: teamLeadNameFrom(row),
      displayOrder: numberOrNull(row.displayOrder),
      isActive,
      statusLabel: getMasterDataStatusLabel(isActive, textOrNull(row.statusCode)),
      rowVersion: numberOrNull(row.rowVersion),
    };
  } catch {
    return null;
  }
}

function normalizeDetails(row: ObjectValue): TeamDetails {
  const isActive = boolOrNull(row.isActive);
  return {
    teamId: requiredId(row.teamId, 'teamId'),
    teamCode: requiredText(row.teamCode, 'teamCode'),
    teamName: requiredText(row.teamName, 'teamName'),
    description: textOrNull(row.description),
    departmentId: requiredId(row.departmentId, 'departmentId'),
    departmentName: textOrNull(row.departmentName),
    locationId: numberOrNull(row.locationId),
    locationName: textOrNull(row.locationName),
    teamLeaderId: numberOrNull(row.teamLeaderId),
    teamLeadName: teamLeadNameFrom(row),
    displayOrder: numberOrNull(row.displayOrder),
    isActive,
    statusLabel: getMasterDataStatusLabel(isActive, textOrNull(row.statusCode)),
    rowVersion: requiredId(row.rowVersion, 'rowVersion'),
  };
}

function buildListParams(
  page: number,
  pageSize: number,
  filters: TeamListFilters = {},
) {
  const params: Record<string, string | number | boolean> = { page, pageSize };
  const search = filters.search?.trim();
  if (search) params.search = search;
  if (filters.departmentId && filters.departmentId > 0) {
    params.departmentId = filters.departmentId;
  }
  if (filters.locationId && filters.locationId > 0) {
    params.locationId = filters.locationId;
  }
  if (filters.isActive === true || filters.isActive === false) {
    params.isActive = filters.isActive;
  }
  return params;
}

export async function getTeams(options: {
  page?: number;
  pageSize?: number;
  filters?: TeamListFilters;
} = {}): Promise<TeamSearchResult> {
  const page = options.page && options.page > 0 ? options.page : 1;
  const pageSize =
    options.pageSize && options.pageSize > 0 ? options.pageSize : 20;

  const response = await apiClient.get<
    Envelope<{
      items?: ObjectValue[];
      pagination?: {
        page?: number;
        pageSize?: number;
        totalRecords?: number;
        totalPages?: number;
      };
    }>
  >(API_ENDPOINTS.TEAMS.LIST, {
    params: buildListParams(page, pageSize, options.filters),
  });

  const data = response.data.data ?? {};
  const items = Array.isArray(data.items)
    ? data.items
        .map(normalizeListItem)
        .filter((item): item is TeamListItem => item != null)
    : [];
  const pagination = data.pagination ?? {};
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const resolvedPageSize = numberOrNull(pagination.pageSize) ?? pageSize;
  const totalPages =
    numberOrNull(pagination.totalPages) ??
    Math.max(1, Math.ceil(totalItems / resolvedPageSize));

  return {
    items,
    page: numberOrNull(pagination.page) ?? page,
    pageSize: resolvedPageSize,
    totalItems,
    totalPages,
  };
}

/** No dedicated GET-by-id. Search pages until the team is found. */
export async function getTeamDetails(teamId: number): Promise<TeamDetails> {
  if (!Number.isInteger(teamId) || teamId <= 0) {
    throw Object.assign(new Error('teamId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  let page = 1;
  let totalPages = 1;
  while (page <= totalPages) {
    const response = await apiClient.get<
      Envelope<{
        items?: ObjectValue[];
        pagination?: { totalPages?: number; totalRecords?: number };
      }>
    >(API_ENDPOINTS.TEAMS.LIST, {
      params: { page, pageSize: DETAILS_PAGE_SIZE },
    });
    const data = response.data.data ?? {};
    const items = Array.isArray(data.items) ? data.items : [];
    const match = items.find(row => numberOrNull(row.teamId) === teamId);
    if (match) return normalizeDetails(match);

    const totalRecords = numberOrNull(data.pagination?.totalRecords) ?? 0;
    totalPages =
      numberOrNull(data.pagination?.totalPages) ??
      Math.max(1, Math.ceil(totalRecords / DETAILS_PAGE_SIZE));
    page += 1;
  }

  throw Object.assign(new Error('Team was not found.'), {
    code: 'TEAM_NOT_FOUND',
  });
}

export async function createTeam(input: TeamCreateInput): Promise<TeamDetails> {
  const existing = await getTeams({ page: 1, pageSize: DETAILS_PAGE_SIZE });
  const body: Record<string, unknown> = {
    departmentId: input.departmentId,
    teamCode: input.teamCode.trim(),
    teamName: input.teamName.trim(),
    description: emptyToNull(input.description),
    displayOrder: nextDisplayOrder(existing.items, existing.totalItems),
  };
  if (input.locationId && input.locationId > 0) {
    body.locationId = input.locationId;
  }
  if (input.teamLeaderId && input.teamLeaderId > 0) {
    body.teamLeaderId = input.teamLeaderId;
  }

  const response = await apiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.TEAMS.CREATE,
    body,
  );
  return normalizeDetails({
    ...body,
    isActive: true,
    rowVersion: 1,
    ...(response.data.data ?? {}),
  });
}

export async function updateTeam(
  teamId: number,
  input: TeamUpdateInput,
): Promise<TeamDetails> {
  if (!Number.isInteger(teamId) || teamId <= 0) {
    throw Object.assign(new Error('teamId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const body: Record<string, unknown> = {
    departmentId: input.departmentId,
    teamName: input.teamName.trim(),
    description: emptyToNull(input.description),
    displayOrder: input.displayOrder ?? 0,
    isActive: input.isActive,
    rowVersion: input.rowVersion,
  };
  if (input.locationId && input.locationId > 0) {
    body.locationId = input.locationId;
  }
  if (input.teamLeaderId && input.teamLeaderId > 0) {
    body.teamLeaderId = input.teamLeaderId;
  }

  const response = await apiClient.put<Envelope<ObjectValue>>(
    API_ENDPOINTS.TEAMS.UPDATE(teamId),
    body,
  );
  return normalizeDetails({
    teamId,
    teamCode: '—',
    ...body,
    ...(response.data.data ?? {}),
  });
}

export async function deactivateTeam(item: TeamDetails): Promise<TeamDetails> {
  return updateTeam(item.teamId, {
    departmentId: item.departmentId,
    locationId: item.locationId,
    teamLeaderId: item.teamLeaderId,
    teamName: item.teamName,
    description: item.description,
    displayOrder: item.displayOrder ?? 0,
    isActive: CONFIGURATION_RECORD_INACTIVE,
    rowVersion: item.rowVersion,
  });
}

function normalizeMember(row: ObjectValue): TeamMember | null {
  try {
    return {
      teamMemberId: requiredId(row.teamMemberId, 'teamMemberId'),
      teamId: requiredId(row.teamId, 'teamId'),
      employeeId: requiredId(row.employeeId, 'employeeId'),
      employeeNumber: textOrNull(row.employeeNumber),
      displayName: textOrNull(row.displayName) ?? 'Employee',
      memberRole: textOrNull(row.memberRole) ?? 'MEMBER',
      effectiveFrom: textOrNull(row.effectiveFrom),
      isPrimaryTeam: boolOrNull(row.isPrimaryTeam),
      isTeamLead:
        boolOrNull(row.isTeamLead) === true ||
        (textOrNull(row.memberRole) || '').toUpperCase() === 'LEADER',
      reportingManagerId: numberOrNull(row.reportingManagerId),
      reportingManagerName: textOrNull(row.reportingManagerName),
    };
  } catch {
    return null;
  }
}

function membersFrom(payload: unknown): TeamMember[] {
  const data = (payload ?? {}) as ObjectValue;
  const rows = Array.isArray(data.items) ? data.items : Array.isArray(payload) ? payload : [];
  return (rows as ObjectValue[])
    .map(normalizeMember)
    .filter((item): item is TeamMember => item != null);
}

export async function getTeamMembers(teamId: number): Promise<TeamMember[]> {
  const response = await apiClient.get<Envelope<{ items?: ObjectValue[] } | ObjectValue[]>>(
    API_ENDPOINTS.TEAMS.MEMBERS(teamId),
  );
  return membersFrom(response.data.data);
}

export async function addTeamMember(
  teamId: number,
  employeeId: number,
  effectiveFrom?: string,
): Promise<TeamMember[]> {
  const body: Record<string, unknown> = { employeeId };
  if (effectiveFrom) body.effectiveFrom = effectiveFrom;
  const response = await apiClient.post<Envelope<{ items?: ObjectValue[] }>>(
    API_ENDPOINTS.TEAMS.MEMBERS(teamId),
    body,
  );
  return membersFrom(response.data.data);
}

export async function removeTeamMember(
  teamId: number,
  employeeId: number,
): Promise<TeamMember[]> {
  const response = await apiClient.delete<Envelope<{ items?: ObjectValue[] }>>(
    API_ENDPOINTS.TEAMS.REMOVE_MEMBER(teamId, employeeId),
  );
  return membersFrom(response.data.data);
}
